import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import { environment } from '../../environments/environment';
import { BuildTarget } from '../../environments/interfaces';

/**
 * Service for ui settings.
 */
@Injectable({
  providedIn: 'root',
})
export class UiSettingsService {

  static CONTEXT_URL_SET_DEVELOP: ContextUrlSet = {
    mapServerUrl: 'https://api-develop.miprorego.io/map/v1/',
    regoUrl: 'https://api-rb-develop.miprorego.io/rego/v1/'
  };

  static CONTEXT_URL_SET_PRODUCTION: ContextUrlSet = {
    mapServerUrl: 'https://api.miprorego.io/map/v1/',
    regoUrl: 'https://api-rb.miprorego.io/rego/v1/'
  };

  private uiContextSubject: Subject<UiContext> = new Subject<UiContext>();
  public uiContext$ = this.uiContextSubject.asObservable();
  private selectedUiContext: UiContext;

  constructor() {
    this.selectedUiContext = this.getContextByBuildTarget(environment.buildTarget);
  }

  /**
   * The list of UI contexts.
   */
  private contexts: UiContext[] = [
    {
      id: 1,
      name: 'production',
      buildTargets: ['master'],
      productionOnly: true,
      contextUrlSets: [
        UiSettingsService.CONTEXT_URL_SET_PRODUCTION
      ]
    },
    {
      id: 2,
      name: 'develop',
      buildTargets: [
        'staging',
        'develop',
        'feature',
        'test',
        'e2e',
        'local-serve',
        'local'],
      productionOnly: false,
      contextUrlSets: [
        UiSettingsService.CONTEXT_URL_SET_DEVELOP
      ]
    },
  ];

  fetchUiContext(): Observable<UiContext> {
    return this.uiContext$;
  }

  /**
   * Returns the selected context.
   */
  getSelectedContext(): UiContext {
    return this.selectedUiContext;
  }

  /**
   * Sets the given context to selected.
   * @param uiContext The selected context.
   */
  selectContext(uiContext: UiContext): void {
    this.selectedUiContext = uiContext;
    this.uiContextSubject.next(uiContext);
  }

  /**
   * Return UI contexts.
   */
  getContexts(): UiContext[] {
    return this.contexts;
  }

  /**
   * Return UI contexts.
   */
  getContextByBuildTarget(target: BuildTarget): UiContext {
    return this.contexts.find(ctx => ctx.buildTargets.includes(target));
  }
}

/**
 * UI context models.
 */
export class UiContext {
  id: number;
  buildTargets: BuildTarget[];
  name: string;
  productionOnly: boolean;
  contextUrlSets: ContextUrlSet[];
}

/**
 * Contains the urls of MapServer and Rego.
 */
export class ContextUrlSet {
  mapServerUrl: string;
  regoUrl: string;
}
